import {sleep} from '@benchling/js-utils';

import {DeepResearchTools} from 'llm/deep-research/messages/DeepResearchProgress';
import * as Net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

const POLL_TIMEOUT_MS = 1000;

export interface SendDeepResearchMessageResponse {
  response: string;
}

// LLM Message Block Types - mirroring Python backend structure
export interface LLMTextBlock {
  type: 'text';
  text: string;
}

export type LLMImageMediaType = 'image/jpeg' | 'image/png' | 'image/webp';

export interface LLMImageBlock {
  type: 'image';
  base64_image: string;
  media_type: LLMImageMediaType;
}

export interface LLMToolCallBlock {
  type: 'tool_call';
  id: string;
  name: DeepResearchTools;
  args: Record<string, unknown>;
}

export interface LLMToolResultBlock {
  type: 'tool_result';
  id: string;
  content: Array<LLMTextBlock | LLMImageBlock>;
}

export interface LLMMessageBlock {
  type: string;
  value: LLMTextBlock | LLMImageBlock | LLMToolCallBlock | LLMToolResultBlock;
}

export enum LLM_ROLES {
  USER = 'user',
  ASSISTANT = 'assistant',
}

export interface LLMMessage {
  role: LLM_ROLES;
  messageBlocks: Array<LLMMessageBlock>;
  createdAt: string;
}

export interface LLMConversationHistory {
  conversationId: string;
  messages: Array<LLMMessage>;
}

export interface LLMChatNode {
  chatId: string;
  conversationId: string;
  parentConversationId: string | null;
  children: Array<LLMChatNode>;
}

export interface LLMChat {
  chatId: string;
  userApiIdentifier: string;
  createdAt: string;
  modifiedAt: string;
  rootNode: LLMChatNode;
}

export interface ProgressDataResponse {
  chatMetadata: LLMChat;
  conversationHistories: Record<string, LLMConversationHistory>;
}
export async function sendDeepResearchMessage(
  chatId: string | undefined,
  message: string,
  updateProgress: (progressData: ProgressDataResponse) => void
): Promise<SendDeepResearchMessageResponse> {
  const initialResponse = await Net.postJSONPromise({
    url: '/1/api/llm/deep-research',
    data: {chatId, message},
  });

  const responseChatId = initialResponse.chatId;

  const statusURL = initialResponse.status_url;
  let taskStatus = await Net.getJSONPromise(statusURL);

  while (taskStatus.status !== 'SUCCESS' && taskStatus.status !== 'FAILURE') {
    await sleep(POLL_TIMEOUT_MS);

    // Poll both the task status and chat progress endpoints
    const [newTaskStatus, chatProgress] = await Promise.all([
      Net.getJSONPromise(statusURL),
      responseChatId ? Net.getJSONPromise(`/1/api/llm/deep-research/chat/${responseChatId}`) : null,
    ]);

    taskStatus = newTaskStatus;
    if (chatProgress) {
      updateProgress(chatProgress);
    }
  }

  if (taskStatus.status === 'FAILURE') {
    throw TaskRunner.flagTaskError(TaskRunner.getFailedTaskErrorResponse(taskStatus));
  }

  return taskStatus.result;
}
